import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';
import KanbanConfigAPI from '../../api/kanbanConfig';

const pendingStageItemRequests = {};

const stageItemsKey = (funnelId, stageId) => `${funnelId || 'all'}:${stageId || 'all'}`;

const updateItemsMap = (_state, items) => {
  (items || []).forEach(item => _state.itemsMap.set(Number(item.id), item));
};

const removeItemFromStageCache = (_state, itemId) => {
  Object.values(_state.stageItems).forEach(stageCache => {
    Object.keys(stageCache.pages || {}).forEach(page => {
      stageCache.pages[page] = stageCache.pages[page].filter(
        item => Number(item.id) !== Number(itemId)
      );
    });
  });
};

const addItemToStageCache = (_state, item) => {
  if (!item?.funnel_id || !item?.funnel_stage) return;

  const cacheKey = stageItemsKey(item.funnel_id, item.funnel_stage);
  const stageCache = _state.stageItems[cacheKey];
  if (!stageCache?.pages?.[1]) return;

  stageCache.pages[1] = [
    item,
    ...stageCache.pages[1].filter(
      existing => Number(existing.id) !== Number(item.id)
    ),
  ];
};

const updateItemInStageCache = (_state, updatedItem) => {
  Object.values(_state.stageItems).forEach(stageCache => {
    Object.keys(stageCache.pages || {}).forEach(page => {
      stageCache.pages[page] = stageCache.pages[page].map(item =>
        Number(item.id) === Number(updatedItem.id) ? { ...item, ...updatedItem } : item
      );
    });
  });
};

const state = {
  isEnabled: false,
  items: [],
  itemsMap: new Map(), // Para buscas O(1)
  stageItems: {},
  loading: false,
  error: null,
  config: {
    title: 'Kanban',
    defaultView: 'kanban',
    webhookUrl: '',
    webhookSecret: '',
    webhookEnabled: false,
    webhookEvents: [],
    autoAssignment: false,
    notificationsEnabled: false,
    supportEmail: '',
    supportPhone: '',
    supportChatHours: '',
    supportChatEnabled: false,
    dragbarEnabled: true,
    quickMessageEnabled: false,
    listViewEnabled: true,
    agendaViewEnabled: true,
    dashboardViewEnabled: true,
    mapViewEnabled: true,
    offersTabTitle: '',
    archivedCleanupEnabled: false,
    archivedCleanupFrequency: 'daily',
    archivedCleanupRetentionDays: 30,
    archivedCleanupLastRunAt: '',
    archivedCleanupLastDeletedAt: '',
    archivedCleanupLastDeletedCount: 0,
    conversationStatusBadgeEnabled: true,
  },
  pagination: {
    currentPage: 1,
    hasMore: true,
    loadingMore: false,
  },
};

const getters = {
  isKanbanEnabled: _state => _state.isEnabled,
  getKanbanItems: _state => _state.items,
  isLoading: _state => _state.loading,
  isLoadingMore: _state => _state.pagination.loadingMore,
  getError: _state => _state.error,
  getKanbanConfig: _state => _state.config,

  getPaginationInfo: _state => _state.pagination,
  hasMorePages: _state => _state.pagination.hasMore,
  // Busca eficiente O(1)
  getItemById: _state => id => _state.itemsMap.get(Number(id)),
  getStageItems: _state => (funnelId, stageId, page = 1) => {
    return _state.stageItems[stageItemsKey(funnelId, stageId)]?.pages?.[page] || null;
  },
  getStagePagination: _state => (funnelId, stageId) => {
    return _state.stageItems[stageItemsKey(funnelId, stageId)]?.pagination || null;
  },

  // Getters específicos para propriedades de configuração
  getKanbanTitle: _state => _state.config.title,
  getWebhookUrl: _state => _state.config.webhookUrl,
  getWebhookEnabled: _state => _state.config.webhookEnabled,
  getDragbarEnabled: _state => _state.config.dragbarEnabled,
  getQuickMessageEnabled: _state => _state.config.quickMessageEnabled,
  getListViewEnabled: _state => _state.config.listViewEnabled !== false,
  getAgendaViewEnabled: _state => _state.config.agendaViewEnabled !== false,
  getDashboardViewEnabled: _state =>
    _state.config.dashboardViewEnabled !== false,
  getMapViewEnabled: _state => _state.config.mapViewEnabled !== false,
  getOffersTabTitle: _state => {
    const v = _state.config.offersTabTitle;
    return typeof v === 'string' && v.trim() ? v.trim() : '';
  },
  getArchivedCleanupEnabled: _state =>
    _state.config.archivedCleanupEnabled === true,
  getArchivedCleanupFrequency: _state =>
    _state.config.archivedCleanupFrequency || 'daily',
  getArchivedCleanupRetentionDays: _state =>
    Number(_state.config.archivedCleanupRetentionDays) || 30,
  getArchivedCleanupLastRunAt: _state =>
    _state.config.archivedCleanupLastRunAt || '',
  getArchivedCleanupLastDeletedAt: _state =>
    _state.config.archivedCleanupLastDeletedAt || '',
  getArchivedCleanupLastDeletedCount: _state =>
    Number(_state.config.archivedCleanupLastDeletedCount) || 0,
  getConversationStatusBadgeEnabled: _state =>
    _state.config.conversationStatusBadgeEnabled !== false,
};

const actions = {
  setKanbanEnabled({ commit }, isEnabled) {
    commit(types.SET_KANBAN_ENABLED, isEnabled);
  },

  async fetchKanbanItems(
    { commit, getters, rootGetters },
    { force = false, funnelId = null, stageId = null, page = 1 } = {}
  ) {
    if (!stageId && getters.isLoading && !force) return;

    const selectedFunnelId =
      funnelId || rootGetters['funnel/getSelectedFunnel']?.id;
    if (!selectedFunnelId) throw new Error('Nenhum funil selecionado');

    const cachedItems = stageId
      ? getters.getStageItems(selectedFunnelId, stageId, page)
      : null;
    if (cachedItems && !force) return cachedItems;

    const requestKey = `${selectedFunnelId}:${stageId || 'all'}:${page}`;
    if (pendingStageItemRequests[requestKey] && !force) {
      return pendingStageItemRequests[requestKey];
    }

    if (!stageId) {
      commit('SET_LOADING', true);
    }
    commit('SET_ERROR', null);

    // Reset paginação apenas no force ou primeira página
    if (force || page === 1) {
      commit('RESET_PAGINATION');
    }

    try {
      pendingStageItemRequests[requestKey] = KanbanAPI.getItems(
        selectedFunnelId,
        page,
        stageId
      ).then(response => {
        const items = response.data?.items || response.data || [];
        const pagination = response.data?.pagination;

        // Atualização baseada no tipo de operação
        if (stageId) {
          commit('SET_STAGE_ITEMS_PAGE', {
            funnelId: selectedFunnelId,
            stageId,
            page,
            items,
            pagination,
          });
        } else if (page > 1) {
          commit('APPEND_ITEMS', items);
        } else {
          commit('SET_KANBAN_ITEMS', items);
        }

        // Atualizar paginação
        if (pagination) {
          commit('SET_PAGINATION_INFO', {
            currentPage: pagination.current_page || page,
            hasMore: pagination.has_more || items.length > 0,
          });
        }

        return items;
      });

      return await pendingStageItemRequests[requestKey];
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      delete pendingStageItemRequests[requestKey];
      if (!stageId) {
        commit('SET_LOADING', false);
      }
    }
  },

  async fetchMoreKanbanItems({ dispatch, getters }, { stageId = null } = {}) {
    if (!getters.hasMorePages || getters.isLoadingMore) return;

    const nextPage = getters.getPaginationInfo.currentPage + 1;
    return dispatch('fetchKanbanItems', { stageId, page: nextPage });
  },

  async createKanbanItem({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.createItem(payload);
      commit('ADD_KANBAN_ITEM', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async moveItemToStage({ commit, getters, dispatch }, { itemId, columnId, funnelId = null }) {
    const numericItemId = Number(itemId);
    const originalItem = getters.getItemById(numericItemId);


    try {
      const updatedItem = await KanbanAPI.moveToStage(numericItemId, columnId, funnelId);

      if (updatedItem) {
        commit('UPDATE_ITEM_WITH_RESPONSE', updatedItem);

        // 🔥 REMOVIDO: Refresh forçado estava causando perda de dados
        // Deixe as colunas se sincronizarem automaticamente via watchers

        return updatedItem;
      } else {
        throw new Error('API moveToStage retornou dados inválidos');
      }
    } catch (error) {
      // Não setar erro no state para não exibir na UI
      // Apenas re-lançar para ser capturado pelo componente
      throw error;
    }
  },

  async deleteKanbanItem({ commit, getters }, itemId) {
    const originalItem = getters.getItemById(itemId);
    commit('REMOVE_KANBAN_ITEM', itemId);

    try {
      await KanbanAPI.deleteItem(itemId);
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_KANBAN_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async archiveKanbanItem({ commit, getters: kanbanGetters }, itemId) {
    const originalItem = kanbanGetters.getItemById(itemId);
    commit('REMOVE_KANBAN_ITEM', itemId);

    try {
      await KanbanAPI.archiveItem(itemId);
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_KANBAN_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async updateItemStructure({ commit, dispatch }, { itemId, structure }) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.updateItemStructure(itemId, {
        item: { item_structure: structure },
      });
      commit('UPDATE_ITEM_STRUCTURE', {
        itemId,
        structure: response.data.item_structure,
      });
      // Item atualizado com sucesso
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateItemDetails({ commit, getters }, { itemId, details }) {
    // Primeiro, obter o item atual para possível rollback
    const originalItem = getters.getItemById(itemId);

    // Atualizar o store local com os dados enviados
    commit('UPDATE_ITEM_DETAILS', { itemId, details });

    try {
      const response = await KanbanAPI.updateItem(itemId, details);

      // Sincronizar com a resposta da API (pode conter campos calculados pelo backend)
      if (response.data) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: response.data });
      }

      return response.data;
    } catch (error) {
      // Rollback em caso de erro
      if (originalItem) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: originalItem });
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async registerActivity({ commit }, { itemId, type, details }) {
    try {
      const currentItem = await KanbanAPI.getItem(itemId);
      const newActivity = {
        id: Date.now(),
        type,
        details,
        created_at: new Date().toISOString(),
        user: this.getters.getCurrentUser,
      };

      const activities = [...(currentItem.data.activities || []), newActivity];
      const payload = {
        ...currentItem.data,
        activities,
        item_details: {
          ...currentItem.data.item_details,
        },
      };

      const response = await KanbanAPI.updateItem(itemId, payload);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async fetchKanbanConfig({ commit }) {
    try {
      const response = await KanbanConfigAPI.getConfig();
      const config = {
        title: response.data.config?.title || 'Kanban',
        defaultView: response.data.config?.default_view || 'kanban',
        webhookUrl: response.data.webhook_url || '',
        webhookSecret: response.data.webhook_secret || '',
        webhookEnabled: response.data.enabled || false,
        webhookEvents: response.data.webhook_events || [],
        autoAssignment: response.data.config?.auto_assignment || false,
        notificationsEnabled: response.data.config?.notifications_enabled || false,
        supportEmail: response.data.config?.support_email || '',
        supportPhone: response.data.config?.support_phone || '',
        supportChatHours: response.data.config?.support_chat_hours || '',
        supportChatEnabled: response.data.config?.support_chat_enabled || false,
        dragbarEnabled: response.data.config?.dragbar_enabled !== false,
        quickMessageEnabled: response.data.config?.quick_message_enabled || false,
        listViewEnabled: response.data.config?.list_view_enabled !== false,
        agendaViewEnabled: response.data.config?.agenda_view_enabled !== false,
        dashboardViewEnabled:
          response.data.config?.dashboard_view_enabled !== false,
        mapViewEnabled: response.data.config?.map_view_enabled !== false,
        offersTabTitle: response.data.config?.offers_tab_title?.trim() || '',
        archivedCleanupEnabled:
          response.data.config?.archived_cleanup_enabled === true,
        archivedCleanupFrequency:
          response.data.config?.archived_cleanup_frequency || 'daily',
        archivedCleanupRetentionDays:
          Number(response.data.config?.archived_cleanup_retention_days) || 30,
        archivedCleanupLastRunAt:
          response.data.config?.archived_cleanup_last_run_at || '',
        archivedCleanupLastDeletedAt:
          response.data.config?.archived_cleanup_last_deleted_at || '',
        archivedCleanupLastDeletedCount:
          Number(response.data.config?.archived_cleanup_last_deleted_count) || 0,
        conversationStatusBadgeEnabled:
          response.data.config?.show_conversation_status_badge !== false,
      };
      commit('SET_KANBAN_CONFIG', config);
      return config;
    } catch (error) {
      const defaultConfig = {
        title: 'Kanban',
        defaultView: 'kanban',
        webhookUrl: '',
        webhookSecret: '',
        webhookEnabled: false,
        webhookEvents: [],
        autoAssignment: false,
        notificationsEnabled: false,
        supportEmail: '',
        supportPhone: '',
        supportChatHours: '',
        supportChatEnabled: false,
        dragbarEnabled: true,
        quickMessageEnabled: false,
        listViewEnabled: true,
        agendaViewEnabled: true,
        dashboardViewEnabled: true,
        mapViewEnabled: true,
        offersTabTitle: '',
        archivedCleanupEnabled: false,
        archivedCleanupFrequency: 'daily',
        archivedCleanupRetentionDays: 30,
        archivedCleanupLastRunAt: '',
        archivedCleanupLastDeletedAt: '',
        archivedCleanupLastDeletedCount: 0,
        conversationStatusBadgeEnabled: true,
      };
      commit('SET_KANBAN_CONFIG', defaultConfig);
      return defaultConfig;
    }
  },

  updateLocalItems({ commit }, items) {
    commit('SET_KANBAN_ITEMS', items);
  },

  async saveKanbanConfig({ commit, dispatch }, configData) {
    try {
      const response = await KanbanConfigAPI.saveConfig(configData);

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      throw error;
    }
  },

  async updateWebhookConfig({ commit, dispatch }, webhookData) {
    try {
      const response = await KanbanConfigAPI.updateWebhookConfig(webhookData);

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      throw error;
    }
  },

  async testWebhook({ commit }) {
    try {
      const response = await KanbanConfigAPI.testWebhook();
      return response;
    } catch (error) {
      throw error;
    }
  },

  async updateQuickMessageConfig({ commit, dispatch, state: localState }, enabled) {
    try {
      // Obter a configuração atual do store
      const currentConfig = { ...localState.config };

      // Fazer merge da nova configuração com a existente
      const updatedConfig = {
        ...currentConfig,
        quick_message_enabled: enabled
      };

      const response = await KanbanConfigAPI.updateConfig({
        config: updatedConfig
      });

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      throw error;
    }
  },


};

const mutations = {
  [types.SET_KANBAN_ENABLED](_state, isEnabled) {
    _state.isEnabled = isEnabled;
  },
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },

  // Items com Map para buscas O(1)
  SET_KANBAN_ITEMS(_state, items) {
    _state.items = items || [];
    _state.itemsMap.clear();
    updateItemsMap(_state, items);
  },

  ADD_KANBAN_ITEM(_state, item) {
    _state.items.push(item);
    _state.itemsMap.set(Number(item.id), item);
    addItemToStageCache(_state, item);
  },

  REMOVE_KANBAN_ITEM(_state, itemId) {
    _state.items = _state.items.filter(item => Number(item.id) !== Number(itemId));
    _state.itemsMap.delete(Number(itemId));
    removeItemFromStageCache(_state, itemId);
  },

  // Mutations simplificadas
  MERGE_STAGE_ITEMS(_state, { items, stageId }) {
    const existingIds = new Set(_state.items.map(item => Number(item.id)));
    const newItems = items.filter(item => !existingIds.has(Number(item.id)));

    if (newItems.length > 0) {
      _state.items.push(...newItems);
      updateItemsMap(_state, newItems);
    }
  },

  APPEND_ITEMS(_state, items) {
    const existingIds = new Set(_state.items.map(item => Number(item.id)));
    const newItems = items.filter(item => !existingIds.has(Number(item.id)));

    if (newItems.length > 0) {
      _state.items.push(...newItems);
      updateItemsMap(_state, newItems);
    }
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    const item = _state.itemsMap.get(Number(itemId));
    if (item) {
      item.funnel_stage = columnId;
      _state.itemsMap.set(item.id, item);
    }
  },
  UPDATE_ITEM_WITH_RESPONSE(_state, updatedItem) {
    const itemId = Number(updatedItem.id);
    const existingItemIndex = _state.items.findIndex(item => Number(item.id) === itemId);

    if (existingItemIndex >= 0) {
      // Atualizar item existente
      _state.items.splice(existingItemIndex, 1, { ...updatedItem });
    } else {
      // Adicionar item novo se não existir
      _state.items.push(updatedItem);
    }

    // Atualizar Map
    _state.itemsMap.set(itemId, updatedItem);
    removeItemFromStageCache(_state, itemId);
    addItemToStageCache(_state, updatedItem);
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const item = _state.itemsMap.get(Number(itemId));
    if (item) {
      item.item_structure = structure;
      _state.itemsMap.set(item.id, item);
    }
  },

  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    const itemIdNum = Number(itemId);
    let item = _state.itemsMap.get(itemIdNum);

    // Se não encontrou no Map, procurar no array
    if (!item) {
      item = _state.items.find(i => i.id === itemIdNum);
    }

    if (item) {
      // Atualizar todas as propriedades do item
      Object.keys(details).forEach(key => {
        if (key === 'item_details' && details.item_details) {
          item.item_details = { ...item.item_details, ...details.item_details };
        } else if (key in item || key in details) {
          item[key] = details[key];
        }
      });

      // Garantir que o item tenha ID correto
      item.id = itemIdNum;

      // Sincronizar tanto o array quanto o Map
      _state.itemsMap.set(itemIdNum, item);
      updateItemInStageCache(_state, item);

      // Atualizar também no array items se existir
      const itemIndex = _state.items.findIndex(i => i.id === itemIdNum);
      if (itemIndex >= 0) {
        _state.items[itemIndex] = item;
      }
    }
  },
  SET_KANBAN_CONFIG(_state, config) {
    _state.config = config;
  },

  SET_STAGE_ITEMS_PAGE(_state, { funnelId, stageId, page, items, pagination }) {
    const cacheKey = stageItemsKey(funnelId, stageId);
    const currentCache = _state.stageItems[cacheKey] || {
      pages: {},
      pagination: null,
    };

    _state.stageItems = {
      ..._state.stageItems,
      [cacheKey]: {
        pages: {
          ...currentCache.pages,
          [page]: items || [],
        },
        pagination: pagination || currentCache.pagination,
      },
    };

    if (page === 1) {
      const existingIds = new Set(_state.items.map(item => Number(item.id)));
      const newItems = (items || []).filter(
        item => !existingIds.has(Number(item.id))
      );
      _state.items.push(...newItems);
    }
    updateItemsMap(_state, items);
  },

  RESET_PAGINATION(_state) {
    _state.pagination = {
      currentPage: 1,
      hasMore: true,
      loadingMore: false,
    };
  },
  SET_PAGINATION_INFO(_state, { currentPage, hasMore }) {
    _state.pagination.currentPage = currentPage;
    _state.pagination.hasMore = hasMore;
  },
  SET_LOADING_MORE(_state, loading) {
    _state.pagination.loadingMore = loading;
  },

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
